import type { ReactNode } from "react";
import { useAuth } from "./useAuth";
import { InteractionStatus } from "./AuthProvider";
import { isAuthConfigured } from "./authConfig";

export interface ProtectedRouteProps {
  /** Content to render when the user is authenticated. */
  children: ReactNode;
}

/**
 * Route guard that requires authentication. Shows a loading state during auth
 * check, redirects to sign-in when not authenticated, and renders children
 * when authenticated. Must be used within AuthProvider.
 */
export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { user, interactionStatus, login } = useAuth();
  const loading = isAuthConfigured && interactionStatus !== InteractionStatus.None;

  if (loading) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-4 py-12"
        role="status"
        aria-live="polite"
        aria-label="Checking authentication"
      >
        <div
          className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent"
          aria-hidden
        />
        <p className="text-base text-text-secondary">Checking sign-in status…</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center gap-6 py-12">
        <h2 className="text-xl font-semibold text-text-primary">
          Sign in required
        </h2>
        <p className="text-base text-text-secondary text-center max-w-md">
          You need to sign in to access this page.
        </p>
        <button
          type="button"
          onClick={() => void login()}
          className="h-10 rounded-component bg-primary px-4 py-2 text-base font-medium text-primary-text hover:bg-primary-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-grey-100 focus-visible:outline-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Sign in to access this page"
        >
          Sign in
        </button>
      </div>
    );
  }

  return <>{children}</>;
}
